import { useState, useEffect } from 'react';

/**
 * UI-129 (loading spec RULED A2 / D4) — hold a value still once a surface is on screen.
 *
 * Returns `value` while `frozen` is false, and the LAST value seen before freezing once
 * `frozen` is true. If `frozen` goes false again the value resumes tracking, so a surface
 * that is torn down and rebuilt (a gym→gym navigation) re-arms by itself.
 *
 * ⚠️⚠️ WHAT THIS IS *FOR*, because the name alone under-sells it: A2 promises that a
 * settled surface may gain detail but must not re-order or reposition. The gym gallery
 * broke that promise on the DOMINANT path — a Google/SSG arrival reveals immediately, so
 * the gallery paints before `getGymPhotoMeta` returns, and the late photo dimensions then
 * both re-sorted the photos and could flip the entire desktop gallery to a three-column
 * strip. This hook is how the page keeps the promise: the dimensions still arrive and
 * still feed non-moving detail, but the values that drive ORDER and LAYOUT stop changing
 * the moment a reader can see them.
 *
 * ⚠️ THE COST IS INHERENT AND WAS RULED, not a bug to fix later: on a pre-baked arrival we
 * learn the shapes only after paint, so the smart-fit sort and the portrait layout never
 * apply there. Waiting for them before revealing was explicitly forbidden (RULED E1 — the
 * reveal must not wait on photos; measured 418ms to DOM-ready against 2,801ms for the last
 * eager photo). **Freezing is the price of not waiting.** Do not "improve" this by
 * delaying the reveal.
 *
 * ⚠️ It deliberately returns the LIVE value while unfrozen rather than always returning
 * held state: the unseeded path is hidden until its meta lands, and it must still get the
 * real sort and layout applied before anything is visible.
 */
export function useFrozenWhile<T>(value: T, frozen: boolean): T {
    const [held, setHeld] = useState<T>(value);

    useEffect(() => {
        if (!frozen) setHeld(value);
    }, [value, frozen]);

    return frozen ? held : value;
}
